import React, { useEffect } from "react";
import { createPortal } from "react-dom";

export type MobileBottomSheetProps = {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
};

/**
 * Full-width bottom sheet for the mobile side-menu selectors (account card,
 * partner, customer). Rendered through a portal to `document.body` so it escapes
 * the mobile drawer's `transform` containing block and can span the full
 * viewport width, anchored to the bottom. The backdrop (and Escape) close it.
 */
export default function MobileBottomSheet({
  open,
  onClose,
  children,
}: MobileBottomSheetProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-end"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        role="presentation"
      />
      <div className="relative max-h-[85vh] w-full overflow-y-auto rounded-t-2xl border-t border-semantic-border-secondary bg-semantic-bg-primary shadow-2xl">
        {children}
      </div>
    </div>,
    document.body
  );
}
